import React, { useEffect, useState } from "react";
import "./FloatingContact.css";

/* ============================================================
   FLOATING CONTACT WIDGET
   Shows fixed WhatsApp / Email / Call buttons on every page.

   HOW TO USE (one time only):
   1. Copy this whole "FloatingContact" folder into your
      src/components/ folder.
   2. Open src/App.js and add:

        import FloatingContact from "./components/FloatingContact/FloatingContact";

      Then render it once, OUTSIDE your <Routes> (so it stays
      fixed on every page), like this:

        function App() {
          return (
            <>
              <Navbar />
              <Routes>
                ... your existing routes ...
              </Routes>
              <Footer />
              <FloatingContact />   // <-- add this line
            </>
          );
        }

   3. Update the CONFIG values below with your real
      WhatsApp number, email, and phone number.
   ============================================================ */

const CONFIG = {
  // Country code + number, NO +, NO spaces, NO dashes
  // Example: India number 81416 61900 -> "918141661900"
  whatsappNumber: "918141661908",

  // Message that auto-fills when someone clicks the WhatsApp button
  whatsappMessage:
    "Hi Savariya Wires & Cables, I would like to enquire about your products.",

  // Email address for the mail button
  email: "info.savariyacable@gmail.com",
  emailSubject: "Enquiry - Savariya Wires & Cables",

  // Phone number for the call button (with country code)
  phoneNumber: "+918141661908",
};

const FloatingContact = () => {
  const [lift, setLift] = useState(0);
  const baseBottom = 36; // must match .floating-contact bottom in CSS
  const margin = 20; // extra breathing room above the footer

  useEffect(() => {
    const footer = document.querySelector("footer");
    if (!footer) return;

    const handleScroll = () => {
      const rect = footer.getBoundingClientRect();
      const overlap = window.innerHeight - rect.top;
      setLift(overlap > 0 ? overlap + margin : 0);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  // Detect if the visitor is on a mobile device
  const isMobile =
    typeof navigator !== "undefined" &&
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  // Mobile: opens the WhatsApp app directly (if installed) — skips the
  // "Continue to Chat" confirmation page that wa.me shows.
  // Desktop: opens WhatsApp Web directly in a new tab.
  const whatsappHref = isMobile
    ? `https://api.whatsapp.com/send?phone=${CONFIG.whatsappNumber}&text=${encodeURIComponent(
        CONFIG.whatsappMessage
      )}`
    : `https://web.whatsapp.com/send?phone=${CONFIG.whatsappNumber}&text=${encodeURIComponent(
        CONFIG.whatsappMessage
      )}`;

  const emailHref = `mailto:${CONFIG.email}?subject=${encodeURIComponent(
    CONFIG.emailSubject
  )}`;

  const callHref = `tel:${CONFIG.phoneNumber}`;

  return (
    <div
      className="floating-contact"
      style={{ bottom: `${baseBottom + lift}px` }}
    >
      <a
        href={whatsappHref}
        className="fc-btn fc-whatsapp"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
        title="Chat on WhatsApp"
      >
        <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor">
          <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.29-1.39a9.9 9.9 0 0 0 4.75 1.21h.01c5.46 0 9.9-4.45 9.9-9.91C21.96 6.45 17.5 2 12.04 2zm0 18.12h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.14.82.84-3.06-.2-.31a8.17 8.17 0 0 1-1.26-4.35c0-4.54 3.7-8.24 8.26-8.24 2.2 0 4.27.86 5.83 2.42a8.19 8.19 0 0 1 2.42 5.83c0 4.55-3.7 8.24-8.25 8.24zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.4-.12-.56.13-.17.25-.65.81-.79.97-.15.17-.29.19-.54.06-.25-.12-1.04-.38-1.99-1.22-.74-.65-1.23-1.46-1.38-1.71-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.15.16-.25.25-.42.08-.17.04-.31-.02-.44-.06-.12-.56-1.35-.77-1.85-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.44.06-.67.31-.23.25-.87.85-.87 2.08s.89 2.41 1.02 2.58c.12.17 1.75 2.67 4.25 3.74.59.26 1.06.41 1.42.52.6.19 1.14.16 1.57.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.15-1.18-.06-.1-.23-.16-.48-.28z"/>
        </svg>
      </a>

      <a
        href={emailHref}
        className="fc-btn fc-email"
        aria-label="Send an email"
        title="Send an email"
      >
        <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
          <path d="M2 4h20v16H2V4zm2 2v.01L12 12l8-5.99V6H4zm16 2.24l-7.4 5.55a1 1 0 0 1-1.2 0L4 8.24V18h16V8.24z"/>
        </svg>
      </a>

      <a
        href={callHref}
        className="fc-btn fc-call"
        aria-label="Call us"
        title="Call us"
      >
        <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
          <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.01-.24 11.36 11.36 0 0 0 3.56.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1 11.36 11.36 0 0 0 .57 3.56 1 1 0 0 1-.25 1.02l-2.2 2.21z"/>
        </svg>
      </a>
    </div>
  );
};

export default FloatingContact;
